import React, { useState } from "react";
import ReactDOM from "react-dom";

import ReactPlayer from "react-player";
import { makeStyles } from "@material-ui/core/styles";
import YoutubePlayer from "../../features/Lesson/components/LessonContent/YoutubePlayer";

const isYouTubeUrl = (url) => /youtube\.com|youtu\.be/.test(url);

const useStyles = makeStyles((theme) => ({
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(10, 16, 28, 0.72)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    zIndex: 999999,
  },
  box: {
    position: "relative",
    width: "100%",
    maxWidth: 1120,
    background: "#ffffff",
    borderRadius: 22,
    overflow: "hidden",
    boxShadow: "0 30px 80px rgba(0, 0, 0, 0.28)",
  },
  closeBtn: {
    position: "absolute",
    top: 16,
    right: 16,
    border: "none",
    background: "rgba(255, 255, 255, 0.96)",
    color: "#0f172a",
    width: 42,
    height: 42,
    borderRadius: "999px",
    fontSize: 24,
    lineHeight: 1,
    cursor: "pointer",
    zIndex: 10,
    boxShadow: "0 6px 18px rgba(15, 23, 42, 0.12)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      transform: "scale(1.03)",
    },
    [theme.breakpoints.down("sm")]: {
      width: 28,
      height: 28,
      fontSize: 16,
      top: 8,
      right: 8,
    },
  },
  content: {
    display: "grid",
    gridTemplateColumns: "1.15fr 1fr",
    minHeight: 620,
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
    },
    [theme.breakpoints.down("sm")]: {
      minHeight: "unset",
    },
  },
  videoSide: {
    background: "#000000",
    position: "relative",
    "& > div": {
      position: "absolute !important",
      top: 0,
      left: 0,
      width: "100% !important",
      height: "100% !important",
    },
    [theme.breakpoints.down("md")]: {
      minHeight: 320,
    },
    [theme.breakpoints.down("sm")]: {
      minHeight: "unset",
      aspectRatio: "16 / 9",
    },
  },
  messageSide: {
    padding: "58px 46px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    [theme.breakpoints.down("md")]: {
      padding: "34px 24px 30px",
    },
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  eyebrow: {
    margin: "0 0 12px",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "1.8px",
    color: "#2563eb",
    textTransform: "uppercase",
  },
  title: {
    margin: "0 0 18px",
    fontSize: 38,
    lineHeight: 1.14,
    fontWeight: 700,
    color: "#0f172a",
    [theme.breakpoints.down("md")]: {
      fontSize: 30,
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: 24,
    },
  },
  subtext: {
    margin: "0 0 18px",
    fontSize: 17,
    lineHeight: 1.72,
    color: "#334155",
    [theme.breakpoints.down("xs")]: {
      fontSize: 15,
    },
  },
  benefits: {
    margin: "4px 0 22px",
    padding: 0,
    listStyle: "none",
    "& li": {
      position: "relative",
      paddingLeft: 30,
      marginBottom: 14,
      fontSize: 16,
      lineHeight: 1.6,
      color: "#1e293b",
      "&::before": {
        content: '"✓"',
        position: "absolute",
        left: 0,
        top: 0,
        width: 20,
        height: 20,
        borderRadius: "999px",
        color: "#2563eb",
        fontWeight: 700,
      },
      [theme.breakpoints.down("xs")]: {
        fontSize: 15,
      },
    },
  },
  footerText: {
    margin: "0 0 18px",
    fontSize: 17,
    lineHeight: 1.72,
    color: "#334155",
    whiteSpace: "pre-line",
    [theme.breakpoints.down("xs")]: {
      fontSize: 15,
    },
  },
  actions: {
    display: "flex",
    gap: 14,
    flexWrap: "wrap",
    marginTop: 8,
    marginBottom: 22,
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
    },
  },
  btnPrimary: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 52,
    padding: "0 22px",
    borderRadius: 12,
    textDecoration: "none",
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
    border: "1px solid #111827",
    background: "#111827",
    color: "#ffffff",
    transition: "all 0.2s ease",
    "&:hover": {
      opacity: 0.94,
      transform: "translateY(-1px)",
    },
    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
  },
  btnSecondary: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 52,
    padding: "0 22px",
    borderRadius: 12,
    textDecoration: "none",
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
    border: "1px solid #e2e8f0",
    background: "#f1f5f9",
    color: "#0f172a",
    transition: "all 0.2s ease",
    "&:hover": {
      background: "#e2e8f0",
    },
    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
  },
  checkboxWrapper: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    fontSize: 14,
    color: "#475569",
    cursor: "pointer",
    userSelect: "none",
    "& input": {
      width: 18,
      height: 18,
      cursor: "pointer",
      accentColor: "#2563eb",
    },
  },
}));

const GuideVideoModal = ({
  videoUrl,
  eyebrow,
  title,
  subtitle,
  bullets = [],
  bodyText,
  hidden = false,
  onDoNotShow,
  onClose,
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState(true);
  const [doNotShow, setDoNotShow] = useState(false);
  const [playing, setPlaying] = useState(false);

  const handleClose = () => {
    setOpen(false);
    if (onClose) onClose();
  };

  const handleCheckboxChange = (e) => {
    const checked = e.target.checked;
    setDoNotShow(checked);
    if (onDoNotShow) onDoNotShow(checked);
  };

  if (!videoUrl || !open || hidden) return null;

  return ReactDOM.createPortal(
    <div
      className={classes.overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div className={classes.box}>
        <button
          className={classes.closeBtn}
          aria-label="Close popup"
          onClick={handleClose}
        >
          &times;
        </button>

        <div className={classes.content}>
          <div className={classes.videoSide}>
            {isYouTubeUrl(videoUrl) ? (
              <YoutubePlayer
                url={videoUrl}
                autoPlay={false}
                playing={playing}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
              />
            ) : (
              <ReactPlayer
                url={videoUrl}
                playing={playing}
                controls
                width="100%"
                height="100%"
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
              />
            )}
          </div>

          <div className={classes.messageSide}>
            {eyebrow && <p className={classes.eyebrow}>{eyebrow}</p>}
            {title && <h2 className={classes.title}>{title}</h2>}
            {subtitle && <p className={classes.subtext}>{subtitle}</p>}

            {bullets.length > 0 && (
              <ul className={classes.benefits}>
                {bullets.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            )}

            {bodyText && <p className={classes.footerText}>{bodyText}</p>}

            <div className={classes.actions}>
              <button className={classes.btnPrimary} onClick={() => setPlaying(p => !p)}>{playing ? 'Stop' : 'Watch now'}</button>
              <button className={classes.btnSecondary} onClick={handleClose}>
                Skip for now
              </button>
            </div>

            {onDoNotShow && (
              <label className={classes.checkboxWrapper}>
                <input
                  type="checkbox"
                  checked={doNotShow}
                  onChange={handleCheckboxChange}
                />
                <span>Do not show this message again</span>
              </label>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default GuideVideoModal;
